import React, {useEffect, useState} from 'react'
import axios from 'axios'
import clsx from 'clsx'
import moment from 'moment'
import {CommonLoading} from '../../../../../Utils/commonLoading'
import toaster from '../../../../../Utils/toaster'
import {ProfileImg} from '../../../../../Utils/fileUrlHelper'
import {KTSVG} from '../../../../../_metronic/helpers'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'

const API_URL = process.env.REACT_APP_API_URL
const MANAGER_VISIBILITY_TYPE = 4

type Props = {
  appointmentId: number
  callLogHeaderId?: number
}

const ScheduledCallActivitiesTab: React.FC<Props> = ({appointmentId, callLogHeaderId}) => {
  const [activities, setActivities] = useState<any>(null)
  const [notes, setNotes] = useState<any>([])
  const [isLoading, setIsLoading] = useState(false)
  const [noteText, setNoteText] = useState('')
  const [isSavingNote, setIsSavingNote] = useState(false)
  const [showAddNote, setShowAddNote] = useState(false)

  useEffect(() => {
    const fetchData = async () => {
      if (!appointmentId && !callLogHeaderId) return
      try {
        setIsLoading(true)
        const targetId = appointmentId || 0
        const query = !appointmentId && callLogHeaderId ? `?callLogHeaderId=${callLogHeaderId}` : ''
        const [actRes, notesRes] = await Promise.all([
          axios.get(`${API_URL}/Appoinment/${targetId}/activities${query}`),
          axios.get(`${API_URL}/Appoinment/${targetId}/notes${query}`),
        ])
        setActivities(actRes?.data)
        setNotes(notesRes?.data || [])
      } catch (e) {
        console.error(e)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [appointmentId, callLogHeaderId])

  const refreshNotes = async () => {
    if (!appointmentId && !callLogHeaderId) return
    try {
      const targetId = appointmentId || 0
      const query = !appointmentId && callLogHeaderId ? `?callLogHeaderId=${callLogHeaderId}` : ''
      const notesRes = await axios.get(`${API_URL}/Appoinment/${targetId}/notes${query}`)
      setNotes(notesRes?.data || [])
    } catch (e) {
      console.error(e)
    }
  }

  const handleSaveNote = async () => {
    const note = noteText.trim()
    if (!note) {
      toaster('error', 'Please write a note')
      return
    }
    if (!appointmentId && !callLogHeaderId) {
      toaster('error', 'Missing appointment/call reference for note')
      return
    }

    setIsSavingNote(true)
    try {
      const result: any = await axios.post(`${API_URL}/Appoinment/notes`, {
        FK_Appoinment: appointmentId > 0 ? appointmentId : null,
        FK_CallHeader: appointmentId > 0 ? null : (callLogHeaderId ?? null),
        note,
        visibilityType: MANAGER_VISIBILITY_TYPE, // always Manager
      })

      if (result?.data?.status === 'S') {
        toaster('success', result?.data?.text ?? 'Successfully Inserted')
        setNoteText('')
        await refreshNotes()
        setShowAddNote(false)
        return
      }

      toaster('error', result?.data?.text ?? 'Failed to save note')
    } catch (e) {
      console.error(e)
      toaster('error', 'Failed to save note')
    } finally {
      setIsSavingNote(false)
    }
  }

  if (isLoading) {
    return (
      <div className='text-center py-5'>
        <CommonLoading />
      </div>
    )
  }

  return (
    <div className='row g-4'>
      <div className='col-lg-12 col-md-12 col-sm-12 col-xl-6 pt-2'>
        <h3 className='fs-6 fw-semibold text-gray-800 mb-5'>Activities</h3>
        <div className='separator my-3'></div>
        <div className='scrollable-notes' style={{maxHeight: '50vh', overflowY: 'auto'}}>
          {activities?.histories?.length > 0 ? (
            activities?.histories?.map((item: any) => (
              <div className='card bg-white mb-2 border border-dotted border-gray-400 border-1 mx-2'>
                <div className='card-body p-4'>
                  <div className='d-flex justify-content-between flex-nowrap w-100'>
                    <div className='d-flex align-items-center w-100'>
                      <div className='symbol-group symbol-hover me-3'>
                        {item.profileImages?.length > 0 ? (
                          item.profileImages?.map((image: any) =>
                            image ? (
                              <div className='symbol symbol-50px symbol-circle d-flex align-items-start'>
                                <ProfileImg
                                  imagePath={image}
                                  alt=''
                                  className=''
                                  style={{objectFit: 'cover'}}
                                />
                              </div>
                            ) : (
                              <div className='symbol symbol-50px symbol-circle overflow-hidden '>
                                <div
                                  className={clsx(
                                    'symbol-label fs-3',
                                    'badge-light-success',
                                    `bg-light-success`
                                  )}
                                >
                                  <span style={{fontSize: '20x'}}>
                                    {(item?.userName ?? '').charAt(0).toUpperCase() +
                                      (item?.userName ?? '').charAt(1).toUpperCase()}
                                  </span>
                                </div>
                              </div>
                            )
                          )
                        ) : (
                          <div className='symbol symbol-50px symbol-circle overflow-hidden'>
                            <div
                              className={clsx(
                                'symbol-label fs-3',
                                'badge-light-success',
                                `bg-light-success`
                              )}
                            >
                              <span style={{fontSize: '20x'}}>
                                {(item?.userName ?? '').charAt(0).toUpperCase() +
                                  (item?.userName ?? '').charAt(1).toUpperCase()}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className='d-flex flex-column w-100'>
                        <div className='d-flex justify-content-between w-100 flex-wrap'>
                          <div className='d-flex flex-wrap'>
                            <span className='fw-bold d-flex align-items-center fs-6 me-1 me-3'>
                              {item?.name}
                            </span>
                            <div className='d-flex'>
                              <span className='badge  badge-secondary px-3 fs-8'>
                                {item?.userName ?? 'N/A'}
                              </span>
                            </div>
                          </div>

                          <div className='d-flex text-gray-800'>
                            <span className='fs-8'>
                              <i className='bi bi-calendar-check me-2'></i>
                              {moment(item.insertedDateTime).format('MM/DD/YY hh:mm A')}
                            </span>
                          </div>
                        </div>

                        <div className='d-flex flex-column flex-1 mt-1'>
                          <span className='fs-7'>{item?.description ?? 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className='card bg-white mb-2 border border-dotted border-gray-400 border-1 mx-2'>
              <div className='py-5 d-flex flex-column align-content-center justify-content-center'>
                <div className='text-center'>
                  <div className='symbol symbol-200px '>
                    <img src='/media/other/nodata.png' alt='' />
                  </div>
                </div>
                <div className='d-flex text-center w-100 align-content-center justify-content-center fw-semibold fs-3 text-gray-400'>
                  No matching records found
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className='col-lg-12 col-md-12 col-sm-12 col-xl-6 pt-2'>
        <div className='d-flex align-items-center justify-content-between mb-5'>
          <h3 className='fs-6 fw-semibold text-gray-800 mb-0'>Notes</h3>

          <OverlayTrigger
            placement='bottom'
            overlay={<Tooltip id='tooltip-filter'>Add Note</Tooltip>}
          >
            <div className='d-flex align-items-center mb-2 me-2'>
              <button
                type='button'
                className='btn btn-sm btn-primary btn-icon px-4'
                onClick={() => setShowAddNote(true)}

              >
                <KTSVG path='/media/icons/duotune/general/gen005.svg' className='svg-icon-muted' />

              </button>
            </div>
          </OverlayTrigger>
        </div>
        <div className='separator my-3'></div>

        {showAddNote && (
          <div
            className='border rounded bg-white p-4 shadow-lg mx-2 mb-3'
            style={{width: '100%'}}
          >
            <div className='d-flex align-items-center justify-content-between mb-4 border-bottom pb-3'>
              <div className='fs-4 fw-bold'>Add Note</div>
              <button
                type='button'
                className='btn btn-link p-0 text-decoration-none'
                onClick={() => {
                  setShowAddNote(false)
                  setNoteText('')
                }}
                disabled={isSavingNote}
              >
                <KTSVG
                  path='/media/icons/duotune/arrows/arr061.svg'
                  className='svg-icon svg-icon-2x'
                />
              </button>
            </div>

            <div className='mb-3'>
              <label className='form-label fs-7 mb-1'>Write a Note</label>
              <textarea
                placeholder='write Note..'
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                className='form-control form-control-sm form-select-white'
                rows={4}
                disabled={isSavingNote}
              ></textarea>
            </div>

            <div className='d-flex justify-content-end gap-2 pt-2 border-top'>
              <button
                type='button'
                className='btn btn-secondary btn-sm'
                onClick={() => {
                  setShowAddNote(false)
                  setNoteText('')
                }}
                disabled={isSavingNote}
              >
                Cancel
              </button>
              <button
                type='button'
                className='btn btn-primary btn-sm'
                onClick={handleSaveNote}
                disabled={isSavingNote || noteText.trim().length === 0}
              >
                {isSavingNote ? 'Saving...' : 'Save Note'}
              </button>
            </div>
          </div>
        )}

        <div className='scrollable-notes' style={{maxHeight: '50vh', overflowY: 'auto'}}>
          {notes?.length > 0 ? (
            notes?.map((item: any) => (
              <div className='card bg-white mb-2 border border-dotted border-gray-400 border-1 mx-2'>
                <div className='card-body p-4'>
                  <div className='d-flex justify-content-between flex-nowrap w-100'>
                    <div className='d-flex align-items-center w-100'>
                      <div className='symbol-group symbol-hover me-3'>
                        {item.avatar ? (
                          <div className='symbol symbol-circle symbol-50px'>
                            <ProfileImg
                              imagePath={item.avatar}
                              alt=''
                              className='rounded-circle'
                              style={{objectFit: 'cover'}}
                            />
                          </div>
                        ) : (
                          <div className='symbol symbol-circle symbol-50px overflow-hidden me-3'>
                            <div
                              className={clsx(
                                'symbol-label fs-3',
                                'badge-light-success',
                                `bg-light-success`
                              )}
                            >
                              <span style={{fontSize: '20px'}}>
                                {(item?.insertedBy ?? '')
                                  .charAt(0)
                                  .toUpperCase() +
                                  (item?.insertedBy ?? '').charAt(1).toUpperCase()}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className='d-flex flex-column w-100'>
                        <div className='d-flex justify-content-between w-100 flex-wrap'>
                          <div className='d-flex flex-wrap'>
                            <span className='fw-bold d-flex align-items-center fs-6 me-1 me-3'>
                              {item?.noteHeader}
                            </span>
                            <div className='d-flex'>
                              <span className='badge  badge-secondary px-3 fs-8'>
                                {item?.insertedByName ?? 'N/A'}
                              </span>
                            </div>
                          </div>

                          <div className='d-flex text-gray-600'>
                            <span className='fs-8'>
                              <i className='bi bi-calendar-check me-2'></i>
                              {moment(item.insertedDateTime).local().format('MM/DD/YY hh:mm A')}
                            </span>
                          </div>
                        </div>

                        <div className='d-flex flex-column flex-1 mt-1'>
                          <span className='fs-7'>{item?.note ?? 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className='card bg-white mb-2 border border-dotted border-gray-400 border-1 mx-2'>
              <div className='py-5 d-flex flex-column align-content-center justify-content-center'>
                <div className='text-center'>
                  <div className='symbol symbol-200px '>
                    <img src='/media/other/nodata.png' alt='' />
                  </div>
                </div>
                <div className='d-flex text-center w-100 align-content-center justify-content-center fw-semibold fs-3 text-gray-400'>
                  No matching records found
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ScheduledCallActivitiesTab
